import { Box } from '@ardoq/layout';
import { Tab, TabList, ViewSelectorBarWrapper } from '@ardoq/tabs';
import {
  MAIN_PANE_LEFT,
  MAIN_PANE_RIGHT,
  MetaInfoViewStatus,
  PaneLocation,
} from 'streams/views/mainContent/types';
import { ViewIds } from '@ardoq/api-types';
import { dispatchAction, connect } from '@ardoq/rxbeach';
import { viewTabClicked } from 'router/navigationActions';
import { CloseIcon, IconName, SplitScreenIcon } from '@ardoq/icons';
import { closePane, showSelectViewsDialog, splitPane } from './actions';
import {
  leftTabOptions$,
  rightTabOptions$,
} from './getTabOptionsStreamWithLocation';
import { GhostButton } from '@ardoq/button';
import { DropdownOptionType } from '@ardoq/dropdown-menu';
import { viewStatusBrands } from 'views/viewStatusBrands';
import { isScenarioMode } from 'models/utils/scenarioUtils';
import { VIEW_SUPPORTING_DIFF } from 'scope/consts';
import { combineLatest, map } from 'rxjs';
import {
  type FocusedChrome,
  focusedChromeOperations,
} from 'scope/focusedChromeOperations';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { t, type I18nLocale } from '@ardoq/i18n';

type TabProps = {
  id: ViewIds;
  name: string;
  label: string;
  tabId: ViewIds;
  viewStatus: MetaInfoViewStatus | undefined;
  isActive?: boolean;
};

type TabBarOwnProps = {
  location: PaneLocation;
  isSplitPane: boolean;
  isRightEditorPaneOpen: boolean;
};
type TabBarStreamedProps = {
  tabs: TabProps[];
  onTabChange: (activeTabId: ViewIds) => void;
  activeTabId: ViewIds;
  canEditWorkspace: boolean;
  focusedChrome: FocusedChrome;
  i18nLocale: I18nLocale;
};
type TabBarProps = TabBarOwnProps & TabBarStreamedProps;

const getStatusTypeByViewStatus = (viewStatus = MetaInfoViewStatus.NONE) => {
  if (viewStatus === MetaInfoViewStatus.NONE) return undefined;
  return viewStatusBrands[viewStatus];
};

const getStatusLabelByViewStatus = (
  i18nLocale: I18nLocale,
  viewStatus = MetaInfoViewStatus.NONE
): string | undefined => {
  switch (viewStatus) {
    case MetaInfoViewStatus.BETA:
      return t(i18nLocale, 'appLayout:viewStatus.beta', 'Beta');
    case MetaInfoViewStatus.SOON_TO_BE_DISCONTINUED:
      return t(
        i18nLocale,
        'appLayout:viewStatus.soonToBeDiscontinued',
        'Soon to be discontinued'
      );
    case MetaInfoViewStatus.DISCONTINUED:
      return t(i18nLocale, 'appLayout:viewStatus.discontinued', 'Discontinued');
    case MetaInfoViewStatus.NEW:
      return t(i18nLocale, 'appLayout:viewStatus.new', 'New');
    case MetaInfoViewStatus.NONE:
      return undefined;
    default: {
      // Exhaustiveness check — adding a new MetaInfoViewStatus member must update this switch.
      const _exhaustive: never = viewStatus;
      return _exhaustive;
    }
  }
};

const LOCATION_TO_EVENT_NAME_SPACE_LOOKUP: Record<PaneLocation, string> = {
  MAIN_PANE_LEFT: 'left visualization tabs',
  MAIN_PANE_RIGHT: 'right visualization tabs',
};

const TabBar = ({
  tabs,
  location,
  onTabChange,
  isSplitPane,
  activeTabId,
  isRightEditorPaneOpen,
  canEditWorkspace,
  focusedChrome,
  i18nLocale,
}: TabBarProps) => {
  if (!(tabs && location)) return null;
  if (tabs.length === 0) return null;
  if (focusedChromeOperations.isFocusedChrome(focusedChrome)) return null;

  return (
    <ViewSelectorBarWrapper>
      <Box padding="xsmall" flexGrow={1} backgroundColor="surfaceDefault">
        <TabList
          clickNamespace={LOCATION_TO_EVENT_NAME_SPACE_LOOKUP[location]}
          overflowType="dropdown"
          onTabChange={tabId => {
            onTabChange(tabId as ViewIds);
          }}
          activeTabId={activeTabId}
          moreButtonLabel={t(i18nLocale, 'common:labels.more', 'More')}
          extraDropdownOptions={
            canEditWorkspace
              ? [
                  {
                    label: t(
                      i18nLocale,
                      'appLayout:manageViews',
                      'Manage views'
                    ),
                    type: DropdownOptionType.BUTTON_OPTION,
                    onClick: () => {
                      dispatchAction(showSelectViewsDialog({ location }));
                    },
                  },
                ]
              : undefined
          }
        >
          {tabs.map(({ viewStatus, ...props }, index) => (
            <Tab
              key={props.id}
              {...props}
              isActive={index === 0}
              statusType={getStatusTypeByViewStatus(viewStatus)}
              badgeTooltipText={getStatusLabelByViewStatus(
                i18nLocale,
                viewStatus
              )}
              rightIconName={
                VIEW_SUPPORTING_DIFF.includes(props.id) && isScenarioMode()
                  ? IconName.SCENARIO
                  : undefined
              }
              clickId={props.tabId}
              rightIconPopoverMessage={t(
                i18nLocale,
                'appLayout:scenarioViewSupport',
                'This view supports all scenario features.'
              )}
            />
          ))}
        </TabList>
      </Box>
      {!isRightEditorPaneOpen &&
        (isSplitPane ? (
          <GhostButton
            onClick={() => dispatchAction(closePane({ location }))}
            data-tooltip-text={t(
              i18nLocale,
              'appLayout:closeColumn',
              'Close column'
            )}
            aria-label={t(i18nLocale, 'appLayout:closeColumn', 'Close column')}
          >
            <CloseIcon />
          </GhostButton>
        ) : (
          <GhostButton
            onClick={() => dispatchAction(splitPane())}
            data-tooltip-text={t(
              i18nLocale,
              'appLayout:openTwoColumnView',
              'Open two-column view'
            )}
            aria-label={t(
              i18nLocale,
              'appLayout:openTwoColumnView',
              'Open two-column view'
            )}
          >
            <SplitScreenIcon />
          </GhostButton>
        ))}
    </ViewSelectorBarWrapper>
  );
};

const leftTabOptionsWithLocale$ = combineLatest([
  leftTabOptions$,
  i18nLocale$,
]).pipe(map(([options, i18nLocale]) => ({ ...options, i18nLocale })));

const rightTabOptionsWithLocale$ = combineLatest([
  rightTabOptions$,
  i18nLocale$,
]).pipe(map(([options, i18nLocale]) => ({ ...options, i18nLocale })));

const TabBarLeft = connect(TabBar, leftTabOptionsWithLocale$);

const TabBarRight = connect(TabBar, rightTabOptionsWithLocale$);

const setActiveLeftTab = (viewId: ViewIds) =>
  dispatchAction(viewTabClicked({ paneLocation: MAIN_PANE_LEFT, viewId }));

const setActiveRightTab = (viewId: ViewIds) =>
  dispatchAction(viewTabClicked({ paneLocation: MAIN_PANE_RIGHT, viewId }));

const ViewPaneTabBar = ({
  location,
  isSplitPane,
  isRightEditorPaneOpen,
}: TabBarOwnProps) =>
  location === MAIN_PANE_LEFT ? (
    <TabBarLeft
      location={MAIN_PANE_LEFT}
      onTabChange={setActiveLeftTab}
      isSplitPane={isSplitPane}
      isRightEditorPaneOpen={isRightEditorPaneOpen}
    />
  ) : (
    <TabBarRight
      location={MAIN_PANE_RIGHT}
      onTabChange={setActiveRightTab}
      isSplitPane={isSplitPane}
      isRightEditorPaneOpen={isRightEditorPaneOpen}
    />
  );
export { ViewPaneTabBar };
